import { useEffect } from 'react';
import { CloudService } from '@/services/CloudService';
import { SimulationConfig, AppSave } from '@/types';
import { useSettingLocks } from './useSettingLocks';

export const useCloudSync = (
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>,
  setCloudSaves?: React.Dispatch<React.SetStateAction<AppSave[]>>
) => {
  const { isSettingLocked } = useSettingLocks('GLOBAL');
  const isCloudLocked = isSettingLocked('cloud_storage');

  useEffect(() => {
    if (isCloudLocked) return;

    const fetchCloudData = async () => {
      // PROACTIVELY CHECK PLAN BEFORE NOISY API CALLS
      // If we are definitely on a FREE plan or not logged in, don't trigger cloud sync
      const cachedProfile = localStorage.getItem('user_profile_cache');
      if (cachedProfile) {
        try {
          const profile = JSON.parse(cachedProfile);
          const plan = (profile.plan || '').toUpperCase();
          if (plan === 'FREE' || plan === 'STARTER') {
             console.log('useCloudSync: Skipping cloud sync for FREE/STARTER plan.');
             return;
          }
        } catch (e) {}
      }

      try {
        const [saves, sounds, melodies, songs] = await Promise.all([
          CloudService.getSaves(),
          CloudService.getSounds(),
          CloudService.getMelodies(),
          CloudService.getSongs()
        ]);

        if (setCloudSaves) setCloudSaves(saves);

        const defaultSave = saves.find((s: any) => s.isDefault);
        
        setConfig(prev => {
          const newConfig = { ...prev };
          
          if (defaultSave && defaultSave.config) {
            console.log("Loaded default save from cloud:", defaultSave.name);
            const savedCfg = defaultSave.config;
            // Only merge valid keys to prevent accidental NaN/undefined propagation
            // We expect at least these core values to be valid
            if (typeof savedCfg.containerRadius === 'number' && !isNaN(savedCfg.containerRadius)) {
                Object.assign(newConfig, savedCfg);
            }
          }

          if (sounds.length > 0) {
            const existingIds = new Set(newConfig.userSounds.map(s => s.id));
            const newSounds = sounds.filter(s => !existingIds.has(s.id));
            newConfig.userSounds = [...newConfig.userSounds, ...newSounds];
          }

          if (melodies.length > 0) {
            const existingIds = new Set(newConfig.customMelodies.map(m => m.id));
            const newMelodies = melodies.map(m => {
                const melodyData = m.data || {};
                return {
                    id: m.id,
                    name: m.name,
                    frequencies: melodyData.frequencies || (Array.isArray(melodyData) ? melodyData : []),
                    format: melodyData.format || 'FREQUENCIES',
                    midiData: melodyData.midiData,
                    hasCloudData: m.hasCloudData || !m.data
                };
            }).filter(m => !existingIds.has(m.id));
            newConfig.customMelodies = [...newConfig.customMelodies, ...newMelodies];
          }

          if (songs.length > 0) {
              // Songs are handled differently in the UI but let's ensure they are available if needed
              // For now, we just fetch them so they are cached in the CloudService if needed
          }

          // Ensure pinned presets are unique and clean
          newConfig.pinnedPresets = Array.from(new Set((newConfig.pinnedPresets || []).map(s => typeof s === 'string' ? s.trim() : s))).filter(Boolean);

          return newConfig;
        });
      } catch (e) {
        console.warn("Could not sync with cloud", e);
      }
    };
    fetchCloudData();
  }, [setConfig]);
};
